import { apiInstance } from '@api/api'
import type {
  ApiParams,
  ApiRequestBody,
  ApiReturnType,
  ErrorResponse,
} from '@api/types'
import type { NullableRequiredProps } from '@core/utils/types'
import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

type Path =
  '/workspaces/{workspace_id}/entity-types/{property_dfn_id}/entities/{standard_id}/generate-overview'

// The spec's path parameters omit {property_dfn_id}, which is part of the URL
type Variables = {
  params: ApiParams<Path, 'post'> & { property_dfn_id: string }
  payload: ApiRequestBody<Path, 'post'>
}

type Args = {
  params: NullableRequiredProps<Variables['params']>
  payload: Variables['payload']
}

type Data = ApiReturnType<Path, 'post', 200>

export function postWorkspaceEntityTypeEntityGenerateOverviewQueryKey(
  args?: Partial<Args>
) {
  const key = [
    'workspaces',
    'entity-type',
    'entity',
    'generate-overview',
    'post',
  ] as const

  return args ? ([...key, args] as const) : key
}

async function postWorkspaceEntityTypeEntityGenerateOverview({
  params: { workspace_id, property_dfn_id, standard_id, ...query },
  payload,
}: Variables) {
  const { data } = await apiInstance.post<Data>(
    `/workspaces/${workspace_id}/entity-types/${property_dfn_id}/entities/${standard_id}/generate-overview`,
    payload,
    { params: query }
  )
  return data
}

export function postWorkspaceEntityTypeEntityGenerateOverviewQueryOptions(
  args: Args
) {
  const { params, payload } = args
  const { workspace_id, property_dfn_id, standard_id } = params

  return queryOptions<Data, AxiosError<ErrorResponse>>({
    queryKey: postWorkspaceEntityTypeEntityGenerateOverviewQueryKey(args),
    queryFn:
      workspace_id && property_dfn_id && standard_id
        ? () =>
            postWorkspaceEntityTypeEntityGenerateOverview({
              params: { ...params, workspace_id, property_dfn_id, standard_id },
              payload,
            })
        : skipToken,
    retry: false,
  })
}

export function usePostWorkspaceEntityTypeEntityGenerateOverview(args: Args) {
  return useQuery(
    postWorkspaceEntityTypeEntityGenerateOverviewQueryOptions(args)
  )
}
